import { useContext, useState } from 'react';
import {
    Alert,
    AlertTitle,
    Avatar,
    Box,
    Divider,
    IconButton,
    Menu,
    MenuItem,
    Snackbar,
    TableCell,
    TableRow,
    Typography,
    Button
} from '@mui/material';
import { MoreVert } from '@mui/icons-material';
import PropTypes from 'prop-types';
// Our Components
import { AuthContext } from 'context/auth-context';
import { UserContext } from 'context/users/userContext';
import { USERS_ACTIONS } from 'context/users/userReducer';
import KYCStatus from './KYCStatus';
import UserStatus from './UserStatus';
// Utils and Helpers
import { USER_ROLES } from 'helpers/constants';
import { useStyles } from '../styles';
import formatPhoneNumber from 'helpers/formatPhoneNumber';
// Assets
import CardHolderIcon from 'assets/usersSection/CardHolderIcon.svg';
import AdminIcon from 'assets/usersSection/AdminIcon.svg';

function UserTableRow({ user }) {
    const classes = useStyles();
    const { businessInfo, userInfo } = useContext(AuthContext);
    const { dispatch } = useContext(UserContext);
    const [anchorEl, setAnchorEl] = useState();
    const [showSnackbar, setShowSnackbar] = useState(false);

    const formatDescription = row => {
        if (!row.job_title && !row.department) {
            return '';
        }
        if (row.job_title && !row.department) {
            return row.job_title;
        }
        if (!row.job_title && row.department) {
            return row.department;
        }
        return `${row.job_title}, ${row.department}`;
    };

    const roleFormat = row => {
        if (row.id_role === USER_ROLES.ADMIN) {
            return (
                <>
                    <Box component="img" src={AdminIcon} alt="Admin" sx={{ verticalAlign: 'middle' }} />
                    <Typography component="span" className={classes.textRow} sx={{ marginLeft: '10px' }}>
                        Admin
                    </Typography>
                </>
            );
        } else {
            return (
                <>
                    <Box component="img" src={CardHolderIcon} alt="Card Holder" sx={{ verticalAlign: 'middle' }} />
                    <Typography component="span" className={classes.textRow} sx={{ marginLeft: '10px' }}>
                        Card Holder
                    </Typography>
                </>
            );
        }
    };

    const handleMenuClick = event => {
        if (businessInfo?.is_frozen) return;
        setAnchorEl(event.currentTarget);
    };

    const handleCloseMenu = () => {
        setAnchorEl(null);
    };

    const handleCloseSnackbar = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setShowSnackbar(false);
    };

    const handleEditClick = () => {
        if (isBusinessMainAdmin()) return setShowSnackbar(true);

        dispatch({ type: USERS_ACTIONS.SET_USER, payload: user });
        dispatch({ type: USERS_ACTIONS.SHOW_USER_FORM_MODAL });
        setAnchorEl(null);
    };

    const handleDeactivateClick = () => {
        if (isBusinessMainAdmin()) return setShowSnackbar(true);

        dispatch({ type: USERS_ACTIONS.SET_USER, payload: user });
        dispatch({ type: USERS_ACTIONS.SHOW_DEACTIVATE_USER_DIALOG });
        setAnchorEl(null);
    };

    const isBusinessMainAdmin = () => businessInfo.main_id_user === user.id;
    const isLoggedUser = () => userInfo.id === user.id;
    const isARMerchant = () => businessInfo.business_ar_id;

    return (
        <>
            <TableRow
                key={user.id}
                sx={{
                    '&:last-child td, &:last-child th': { border: 0 }
                }}>
                <TableCell scope="row">
                    <Typography component="div" className={classes.textRowPrimary}>
                        {user.first_name + ' ' + user.last_name}
                    </Typography>
                    <Typography variant="body1" component="div" className={classes.textRowDescription}>
                        {formatDescription(user)}
                    </Typography>
                </TableCell>
                <TableCell>
                    <Typography component="div" className={classes.textRow}>
                        {user.email}
                    </Typography>
                    <Typography variant="body1" component="div" className={classes.textRowDescription}>
                        {formatPhoneNumber(user.phone_number)}
                    </Typography>
                </TableCell>
                <TableCell>{roleFormat(user)}</TableCell>
                <TableCell className={classes.textRow} sx={{ textAlign: 'center' }}>
                    {user.active_cards}
                </TableCell>
                <TableCell>
                    <UserStatus user={user} />
                </TableCell>
                <TableCell>
                    <KYCStatus user={user} />
                </TableCell>
                <TableCell sx={{ textAlign: 'center' }}>
                    {!user?.is_disabled && !isBusinessMainAdmin() && !isLoggedUser() && user.person_id && (
                        <>
                            <IconButton onClick={handleMenuClick}>
                                <Avatar className="action-menu-btn">
                                    <MoreVert />
                                </Avatar>
                            </IconButton>
                            <Menu anchorEl={anchorEl} open={!!anchorEl} onClose={handleCloseMenu}>
                                <MenuItem onClick={handleEditClick}>Edit</MenuItem>
                                <Divider />
                                <MenuItem onClick={handleDeactivateClick}>Deactivate</MenuItem>
                            </Menu>
                        </>
                    )}
                    {isARMerchant() && !user.person_id && (
                        <>
                            <Button variant="outlined" onClick={handleEditClick}>
                                Complete Profile
                            </Button>
                        </>
                    )}
                </TableCell>
            </TableRow>
            <Snackbar
                open={showSnackbar}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                autoHideDuration={5000}>
                <Alert severity="error" variant="filled" sx={{ width: '100%' }}>
                    <AlertTitle>Action Denied</AlertTitle>
                    Business Main Admin can't be edited
                </Alert>
            </Snackbar>
        </>
    );
}

UserTableRow.propTypes = {
    user: PropTypes.object.isRequired
};

export default UserTableRow;
